<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Button,
		Container,
		Modal,
		Select,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	//Components
	import VcSettings from './VCSettings.svelte';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getWizardVComponent,
		getVCTypeOptions,
		getInitVCSettings,
		checkVCTypeOptionsFWSupport,
	} from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	import { getVirtualComponents } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { VirtualComponentInterface, VirtualComponentTypes } from 'src/types/VirtualComponents';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;
	export let showWizard = false;
	export let selectedItems: string[] = null;
	export let filterType: VirtualComponentTypes = null;
	export let initSettings: VirtualComponentInterface['config'] = null;

	const dispatch = createEventDispatcher();
	const wizardItems = getWizardVComponent();
	const typeOptions = getVCTypeOptions().filter((option) =>
		checkVCTypeOptionsFWSupport(device, option.min_version),
	);

	const onTypeSelect = (e: CustomEvent) => {
		type = e.detail.value;
		settings = getInitVCSettings(type);
		component = getComponentLabel(type);
	};

	const getComponentLabel = (type: VirtualComponentTypes): string => {
		return `${type}:${200 + getVirtualComponents(device.id, type).length}`;
	};

	const save = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Virtual.Add', {
			type,
			config: settings,
		});
		const resToastMessages = {
			success: $format('vc_template_create_success', {
				default: 'Virtual component created successfully.',
			}),
			danger: null,
		};
		const resData = res?.data || res?.result;
		const isok = res?.isok && resData?.id != null;
		const errorCode = res?.error?.device_error?.code || res?.errors?.code;
		if (isok) {
			const deviceComponent = `${type}:${resData.id}`;
			if (selectedItems) {
				selectedItems.push(deviceComponent);
			}
			dispatch('save', deviceComponent);
			showWizard = false;
		} else if (errorCode === -108) {
			resToastMessages.danger = $format('vc_template_create_reached_error', {
				default: 'Reached the maximum 10 of virtual components.',
			});
		}
		onSettingsResponse({ ...res, isok }, device, resToastMessages);
		waitForResponse = false;
	};

	let waitForResponse = false;
	let activeStep = wizardItems[filterType ? 1 : 0];
	let settingsError: boolean;
	let type: VirtualComponentTypes = filterType || typeOptions[0].value;
	let settings: VirtualComponentInterface['config'] = {
		...getInitVCSettings(type),
		...(initSettings || {}),
	};
	let component: string = getComponentLabel(type);
	let editEnumModal = false;
</script>

<Modal
	title={$format('virtual_components_create_component', {
		default: 'Create virtual component',
	})}
	showModal={showWizard}
	on:close={() => {
		showWizard = false;
	}}
	removeScrollPadding
>
	{#if !filterType}
		<Wizard
			slot="toolbar"
			activeStepNumber={activeStep.activeIndex}
			stepsTotal={wizardItems.length}
			activeStepTitle={activeStep.title}
		>
			{#each wizardItems as item, i (i)}
				<WizardItem
					on:click={() => {
						activeStep = wizardItems[i];
					}}
					active={activeStep.activeIndex === i}
					title={item.title}
					indicatorColor={$showNewLayout ? 'color-2' : undefined}
				/>
			{/each}
		</Wizard>
	{/if}

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight={true}>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i} class="pr-0 pl-0">
					<Container padding="scrollPaddings">
						{#if activeStep.activeIndex != wizardItems.length - 1}
							<Text element="h2" class="text-capitalize mb-m">
								{item.subtitle}
							</Text>
						{/if}
						{#if i == 0}
							<Select
								aria-label={$format('select_component_type', {
									default: 'Select component type',
								})}
								class="mb-l"
								size="m"
								icon="fas fa-cube"
								value={typeOptions[0]}
								options={typeOptions}
								on:change={onTypeSelect}
								id="select-qop0bg0"
							/>
						{:else if i == 1}
							<VcSettings
								{type}
								bind:settings
								bind:settingsError
								bind:editEnumModal
								{component}
							/>
						{/if}
					</Container>
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if !editEnumModal}
			{#if activeStep.activeIndex > 0 && !filterType}
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					icon="fas fa-chevron-left"
					responsiveSize={{
						sm: 'm',
					}}
					variant="secondary"
					on:click={() => {
						if (activeStep.activeIndex > 0) {
							activeStep = wizardItems[activeStep.activeIndex - 1];
						}
					}}
				>
					{$format('button_back', { default: 'Back' })}
				</Button>
			{/if}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				icon={activeStep.activeIndex == wizardItems.length - 1 ? 'fas fa-save' : ''}
				rightIcon={activeStep.activeIndex == wizardItems.length - 1
					? ''
					: 'fas fa-chevron-right'}
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForResponse}
				disabled={waitForResponse ||
					(activeStep.activeIndex == wizardItems.length - 1 && settingsError)}
				on:click={() => {
					if (activeStep.activeIndex == wizardItems.length - 1) {
						save();
					} else {
						activeStep = wizardItems[activeStep.activeIndex + 1];
					}
				}}
			>
				{activeStep.activeIndex == wizardItems.length - 1
					? $format('button_save', { default: 'Save' })
					: $format('button_next', { default: 'Next' })}
			</Button>
		{/if}
	</div>
</Modal>
